import { z } from "zod";
import { IsoTimestamp, Pence } from "../primitives.js";

/**
 * The figures a household types in rather than derives — its income above
 * all. Statements say what landed in an account; they cannot say what a
 * salary *is*, because a bonus month, a refund or money moved between two
 * people's accounts all land as income too. A plan built on the average of
 * those is a plan built on noise.
 *
 * Flat key/value, so each figure is edited and audited on its own. The key is
 * a dotted name: `income.<slug>` for each line of take-home pay, and
 * `savings.buffer` for what the household could fall back on.
 */
export const PlanKey = z
  .string()
  .regex(/^[a-z]+(\.[a-z0-9-]+)+$/, "A plan key looks like income.mark")
  .max(60);

export const PlanValue = z.object({
  key: PlanKey,
  /** Monthly for an income line; a balance for the savings buffer. */
  valuePence: Pence,
  label: z.string().nullable(),
  sortOrder: z.number().int(),
  updatedAt: IsoTimestamp,
});

export const PlanList = z.object({ data: z.array(PlanValue) });

export const PlanValueInput = z.strictObject({
  valuePence: Pence.min(0, "A plan figure cannot be negative"),
  label: z.string().trim().min(1).max(60).nullable().default(null),
  sortOrder: z.number().int().optional(),
});

export type PlanKey = z.infer<typeof PlanKey>;
export type PlanValue = z.infer<typeof PlanValue>;
export type PlanList = z.infer<typeof PlanList>;
export type PlanValueInput = z.infer<typeof PlanValueInput>;
